import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { fetchAuditLogs, AuditLogEntry } from '@/services/teamService';
import { format, isToday, isYesterday } from 'date-fns';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Clock,
  UserPlus,
  UserCog,
  UserMinus,
  Search,
  SlidersHorizontal,
  ChevronUp,
  ChevronDown,
  DownloadCloud,
  RefreshCw,
  AlertCircle,
  Info,
  User,
  Calendar,
  FileText,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetFooter,
  SheetClose,
} from '@/components/ui/sheet';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { ROUTES } from '@/config/routes';
import { useTeampageTranslations } from '@/hooks/useTeampageTranslations';
import { logDebug, logError } from '@/utils/errorHandler';

// Type definitions
type SortField = 'timestamp' | 'action' | 'user';
type SortOrder = 'asc' | 'desc';
type FilterOptions = {
  action: string;
  dateRange: string;
  search: string;
};

// Helper functions
const getActionIcon = (action: string) => {
  switch (action) {
    case 'invite':
      return <UserPlus className="mr-1 size-4" />;
    case 'role_change':
      return <UserCog className="mr-1 size-4" />;
    case 'remove':
      return <UserMinus className="mr-1 size-4" />;
    default:
      return <AlertCircle className="mr-1 size-4" />;
  }
};

const getActionColor = (action: string) => {
  switch (action) {
    case 'invite':
      return 'bg-green-100 text-green-800 border-green-200 dark:bg-green-950/50 dark:text-green-300 dark:border-green-800';
    case 'role_change':
      return 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-950/50 dark:text-blue-300 dark:border-blue-800';
    case 'remove':
      return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-950/50 dark:text-red-300 dark:border-red-800';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600';
  }
};

const formatActionText = (action: string) => {
  return action.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const ActionDescription: React.FC<{ log: AuditLogEntry }> = ({ log }) => {
  const i18n = useTeampageTranslations();

  switch (log.action) {
    case 'invite':
      return i18n.history.actions.inviteDescription(
        log.details.email ?? 'a user',
        log.details.role ?? 'member'
      );
    case 'role_change':
      return i18n.history.actions.roleChangeDescription(
        log.details.email ?? 'a user',
        log.details.from ?? 'previous role',
        log.details.to ?? 'new role'
      );
    case 'remove':
      return i18n.history.actions.removeDescription(
        log.details.email ?? 'a user',
        log.details.role ?? 'member'
      );
    default:
      return i18n.history.actions.defaultDescription(log.action);
  }
};

// Loading Skeleton Component
const AuditLogSkeleton: React.FC = () => (
  <div className="space-y-4">
    {Array.from({ length: 5 }).map((_, index) => (
      <div key={index} className="rounded-lg bg-card p-4 shadow">
        <div className="flex items-start justify-between">
          <div className="w-full">
            <div className="flex items-center">
              <Skeleton className="mr-2 h-6 w-24 rounded" />
              <Skeleton className="h-4 w-32 rounded" />
            </div>
            <div className="mt-2">
              <Skeleton className="mt-2 h-4 w-full rounded" />
            </div>
          </div>
        </div>
      </div>
    ))}
  </div>
);

// Empty State Component
const EmptyState: React.FC<{ onRefresh: () => void }> = ({ onRefresh }) => {
  const i18n = useTeampageTranslations();

  return (
    <Card className="w-full">
      <CardHeader className="pb-4">
        <CardTitle className="text-center text-xl">{i18n.history.noRecordsFound}</CardTitle>
        <CardDescription className="text-center">{i18n.history.recordsDescription}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col items-center pb-6">
        <div className="mb-4 rounded-full bg-muted/50 p-6">
          <Clock className="size-8 text-muted-foreground" />
        </div>
        <p className="max-w-md text-center text-sm text-muted-foreground">
          {i18n.history.actionsDescription}
        </p>
      </CardContent>
      <CardFooter className="flex justify-center pb-8">
        <Button variant="outline" onClick={onRefresh} className="flex items-center">
          <RefreshCw className="mr-2 size-4" />
          {i18n.buttons.refresh}
        </Button>
      </CardFooter>
    </Card>
  );
};

// Filter Sheet Component
const FilterSheet: React.FC<{
  filters: FilterOptions;
  onFiltersChange: (filters: FilterOptions) => void;
}> = ({ filters, onFiltersChange }) => {
  const i18n = useTeampageTranslations();
  const [tempFilters, setTempFilters] = useState<FilterOptions>(filters);

  const handleApplyFilters = () => {
    onFiltersChange(tempFilters);
  };

  const handleReset = () => {
    const resetFilters = {
      action: 'all',
      dateRange: 'all',
      search: '',
    };
    setTempFilters(resetFilters);
    onFiltersChange(resetFilters);
  };

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="outline" className="flex items-center gap-2">
          <SlidersHorizontal className="size-4" />
          {i18n.history.filterByAction}
        </Button>
      </SheetTrigger>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>{i18n.history.filterByAction}</SheetTitle>
          <SheetDescription>{i18n.history.filterDescription}</SheetDescription>
        </SheetHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <h4 className="text-sm font-medium">{i18n.history.action}</h4>
            <Select
              value={tempFilters.action}
              onValueChange={value => setTempFilters({ ...tempFilters, action: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder={i18n.history.allActions} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{i18n.history.allActions}</SelectItem>
                <SelectItem value="invite">{i18n.history.actions.invite}</SelectItem>
                <SelectItem value="role_change">{i18n.history.actions.role_change}</SelectItem>
                <SelectItem value="remove">{i18n.history.actions.remove}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <h4 className="text-sm font-medium">{i18n.history.filterByDate}</h4>
            <Select
              value={tempFilters.dateRange}
              onValueChange={value => setTempFilters({ ...tempFilters, dateRange: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder={i18n.history.anyTime} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{i18n.history.anyTime}</SelectItem>
                <SelectItem value="today">{i18n.history.today}</SelectItem>
                <SelectItem value="week">{i18n.history.lastWeek}</SelectItem>
                <SelectItem value="month">{i18n.history.lastMonth}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <SheetFooter className="pt-4">
          <Button variant="outline" onClick={handleReset}>
            {i18n.history.resetFilters}
          </Button>
          <SheetClose asChild>
            <Button onClick={handleApplyFilters}>{i18n.history.applyFilters}</Button>
          </SheetClose>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

// Custom pagination component that accepts the props we need
const PaginationControl: React.FC<{
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number | React.SetStateAction<number>) => void;
}> = ({ currentPage, totalPages, onPageChange }) => {
  // Generate an array of page numbers to display
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;

    if (totalPages <= maxPagesToShow) {
      // If we have fewer pages than our maximum, show all pages
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // Always show first page
      pageNumbers.push(1);

      // Calculate start and end of pages to show
      let start = Math.max(2, currentPage - 1);
      let end = Math.min(totalPages - 1, currentPage + 1);

      // Adjust if we're near the beginning
      if (currentPage <= 3) {
        end = 4;
      }

      // Adjust if we're near the end
      if (currentPage >= totalPages - 2) {
        start = totalPages - 3;
      }

      // Add ellipsis if needed at the beginning
      if (start > 2) {
        pageNumbers.push('ellipsis-start');
      }

      // Add the pages
      for (let i = start; i <= end; i++) {
        pageNumbers.push(i);
      }

      // Add ellipsis if needed at the end
      if (end < totalPages - 1) {
        pageNumbers.push('ellipsis-end');
      }

      // Always show last page
      pageNumbers.push(totalPages);
    }

    return pageNumbers;
  };

  const pageNumbers = getPageNumbers();

  return (
    <Pagination>
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
          />
        </PaginationItem>

        {pageNumbers.map((pageNumber, i) => {
          if (pageNumber === 'ellipsis-start' || pageNumber === 'ellipsis-end') {
            return (
              <PaginationItem key={`ellipsis-${i}`}>
                <span className="flex size-9 items-center justify-center">...</span>
              </PaginationItem>
            );
          }

          return (
            <PaginationItem key={pageNumber}>
              <PaginationLink
                isActive={currentPage === pageNumber}
                onClick={() => onPageChange(pageNumber as number)}
              >
                {pageNumber}
              </PaginationLink>
            </PaginationItem>
          );
        })}

        <PaginationItem>
          <PaginationNext
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages || totalPages === 0}
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
};

const ActionBadge: React.FC<{ action: string }> = ({ action }) => {
  const getActionBorderColor = (action: string) => {
    switch (action) {
      case 'invite':
        return 'border-green-200';
      case 'role_change':
        return 'border-blue-200';
      case 'remove':
        return 'border-red-200';
      default:
        return 'border-gray-200';
    }
  };

  return (
    <div
      className={`inline-flex items-center rounded-full border px-2 py-1 text-xs font-medium ${getActionColor(action)} ${getActionBorderColor(action)}`}
    >
      {getActionIcon(action)}
      {formatActionText(action)}
    </div>
  );
};

// DetailDrawer component for rich detail display
const DetailDrawer: React.FC<{
  log: AuditLogEntry | null;
  open: boolean;
  onClose: () => void;
}> = ({ log, open, onClose }) => {
  const i18n = useTeampageTranslations();

  if (!log) return null;

  const formatDetailValue = (value: any) => {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  };

  const renderHumanReadableDetails = () => {
    if (!log.details) return null;

    // Format based on action type
    switch (log.action) {
      case 'invite':
        return (
          <div className="space-y-4">
            <div className="rounded-md border border-green-100 bg-green-50 p-3">
              <h4 className="mb-2 flex items-center text-sm font-medium text-green-800">
                <UserPlus className="mr-1 size-4" />
                {i18n.history.details.invitationDetails}
              </h4>
              <div className="space-y-2">
                <div className="grid grid-cols-3 gap-2">
                  <div className="text-xs font-medium text-green-700">
                    {i18n.history.details.email}
                  </div>
                  <div className="col-span-2 text-xs">{log.details.email || '—'}</div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="text-xs font-medium text-green-700">
                    {i18n.history.details.role}
                  </div>
                  <div className="col-span-2 text-xs">{log.details.role || '—'}</div>
                </div>
                {log.details.message && (
                  <div className="grid grid-cols-3 gap-2">
                    <div className="text-xs font-medium text-green-700">
                      {i18n.history.details.message}
                    </div>
                    <div className="col-span-2 text-xs">{log.details.message}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'role_change':
        return (
          <div className="space-y-4">
            <div className="rounded-md border border-blue-100 bg-blue-50 p-3">
              <h4 className="mb-2 flex items-center text-sm font-medium text-blue-800">
                <UserCog className="mr-1 size-4" />
                {i18n.history.details.roleChangeDetails}
              </h4>
              <div className="space-y-2">
                <div className="grid grid-cols-3 gap-2">
                  <div className="text-xs font-medium text-blue-700">{i18n.activity.user}</div>
                  <div className="col-span-2 text-xs">{log.details.email || '—'}</div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="text-xs font-medium text-blue-700">
                    {i18n.history.details.from}
                  </div>
                  <div className="col-span-2 rounded bg-red-50 px-1 py-0.5 text-xs line-through">
                    {log.details.from || '—'}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="text-xs font-medium text-blue-700">{i18n.history.details.to}</div>
                  <div className="col-span-2 rounded bg-green-50 px-1 py-0.5 text-xs">
                    {log.details.to || '—'}
                  </div>
                </div>
                {log.details.reason && (
                  <div className="grid grid-cols-3 gap-2">
                    <div className="text-xs font-medium text-blue-700">
                      {i18n.history.details.reason}
                    </div>
                    <div className="col-span-2 text-xs">{log.details.reason}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'remove':
        return (
          <div className="space-y-4">
            <div className="rounded-md border border-red-100 bg-red-50 p-3">
              <h4 className="mb-2 flex items-center text-sm font-medium text-red-800">
                <UserMinus className="mr-1 size-4" />
                {i18n.history.details.removalDetails}
              </h4>
              <div className="space-y-2">
                <div className="grid grid-cols-3 gap-2">
                  <div className="text-xs font-medium text-red-700">{i18n.activity.user}</div>
                  <div className="col-span-2 text-xs">{log.details.email || '—'}</div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="text-xs font-medium text-red-700">
                    {i18n.history.details.role}
                  </div>
                  <div className="col-span-2 text-xs">{log.details.role || '—'}</div>
                </div>
                {log.details.reason && (
                  <div className="grid grid-cols-3 gap-2">
                    <div className="text-xs font-medium text-red-700">
                      {i18n.history.details.reason}
                    </div>
                    <div className="col-span-2 text-xs">{log.details.reason}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      default:
        // For any other action type, display a generic format
        return (
          <div className="space-y-4">
            <div className="rounded-md border border-gray-100 bg-gray-50 p-3">
              <h4 className="mb-2 flex items-center text-sm font-medium text-gray-800">
                <Info className="mr-1 size-4" />
                {i18n.history.details.eventDetails}
              </h4>
              <div className="space-y-2">
                {Object.entries(log.details).map(([key, value]) => (
                  <div key={key} className="grid grid-cols-3 gap-2">
                    <div className="text-xs font-medium capitalize text-gray-700">
                      {key.replace(/_/g, ' ')}
                    </div>
                    <div className="col-span-2 font-mono text-xs">{formatDetailValue(value)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <Sheet open={open} onOpenChange={isOpen => !isOpen && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-md">
        <SheetHeader className="border-b pb-4">
          <SheetTitle>{i18n.history.detailsTitle}</SheetTitle>
        </SheetHeader>

        <div className="space-y-6 py-6">
          {/* Meta information */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-gray-500">{i18n.history.eventInformation}</h3>

            <div className="grid grid-cols-[20px_1fr] gap-3">
              <Calendar className="size-5 text-gray-400" />
              <div className="space-y-1 text-sm">
                <div className="font-medium">{i18n.history.timestamp}</div>
                <div className="text-gray-600">{format(new Date(log.timestamp), 'PPpp')}</div>
              </div>

              <User className="size-5 text-gray-400" />
              <div className="space-y-1 text-sm">
                <div className="font-medium">{i18n.history.performedBy}</div>
                <div className="text-gray-600">
                  {log.user ? (
                    <>
                      <div>{log.user.name || i18n.history.unnamedUser}</div>
                      <div className="text-xs text-gray-500">{log.user.email}</div>
                    </>
                  ) : (
                    i18n.history.system
                  )}
                </div>
              </div>

              <FileText className="size-5 text-gray-400" />
              <div className="space-y-1 text-sm">
                <div className="font-medium">{i18n.history.target}</div>
                <div className="capitalize text-gray-600">
                  {log.target_type.replace(/_/g, ' ')} #{log.target_id}
                </div>
              </div>
            </div>
          </div>

          {/* Action details */}
          <div className="space-y-4 pt-2">
            <h3 className="text-sm font-medium text-gray-500">{i18n.history.additionalDetails}</h3>
            {renderHumanReadableDetails()}
          </div>

          {/* Raw JSON for developers */}
          <Accordion type="single" collapsible className="pt-2">
            <AccordionItem value="raw-json" className="border-0">
              <AccordionTrigger className="py-2 text-xs text-gray-500 hover:text-gray-700 hover:no-underline">
                {i18n.history.showRawJson}
              </AccordionTrigger>
              <AccordionContent>
                <div className="overflow-x-auto rounded bg-gray-50 p-3 font-mono text-xs">
                  <pre>{JSON.stringify(log.details, null, 2)}</pre>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default function TeamHistoryPage() {
  const i18n = useTeampageTranslations();
  const { orgId } = useParams<{ orgId: string }>();
  const { user } = useAuth();

  // Use the non-null assertion as needed
  const orgID = orgId ?? user?.organization_id ?? '1';

  // State for pagination, sorting and filtering
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [sortField, setSortField] = useState<SortField>('timestamp');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [filters, setFilters] = useState<FilterOptions>({
    action: 'all',
    dateRange: 'all',
    search: '',
  });
  const [searchInput, setSearchInput] = useState('');

  // State for detail drawer
  const [selectedLog, setSelectedLog] = useState<AuditLogEntry | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  // Helper function to group logs by date
  const groupLogsByDate = (logs: AuditLogEntry[]) => {
    const groups: { [key: string]: AuditLogEntry[] } = {};

    logs.forEach(log => {
      const date = new Date(log.timestamp);
      let dateKey: string;

      if (isToday(date)) {
        dateKey = i18n.history.today;
      } else if (isYesterday(date)) {
        dateKey = i18n.history.yesterday;
      } else {
        dateKey = format(date, 'PPP');
      }

      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(log);
    });

    // Sort groups by date (newest first)
    const sortedGroups = Object.entries(groups).sort(([dateA], [dateB]) => {
      // Handle 'Today' and 'Yesterday' specially
      if (dateA === i18n.history.today) return -1;
      if (dateB === i18n.history.today) return 1;
      if (dateA === i18n.history.yesterday) return -1;
      if (dateB === i18n.history.yesterday) return 1;

      // For other dates, parse and compare
      const parsedA = new Date(dateA);
      const parsedB = new Date(dateB);
      return parsedB.getTime() - parsedA.getTime();
    });

    return sortedGroups;
  };

  const handleViewDetails = (log: AuditLogEntry) => {
    setSelectedLog(log);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedLog(null);
  };

  // Function to handle search input changes with debouncing
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInput(value);

    // Debounce search to avoid too many re-renders
    const timeoutId = setTimeout(() => {
      setFilters(prev => ({
        ...prev,
        search: value,
      }));
      setPage(1); // Reset to first page on new search
    }, 300);

    return () => clearTimeout(timeoutId);
  };

  // Query to fetch audit logs
  const {
    data: allLogs = [],
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['auditLogs', orgID, filters],
    queryFn: async () => {
      try {
        // In a real implementation, these filters would be passed to the API
        const data = await fetchAuditLogs(orgID);
        logDebug('Fetched audit logs', {
          component: 'TeamHistoryPage',
          action: 'fetchAuditLogs',
          orgID,
          count: data.length,
        });
        return data;
      } catch (error) {
        logError(error as Error, {
          component: 'TeamHistoryPage',
          action: 'fetchAuditLogs',
          organizationId: orgID,
        });
        throw error;
      }
    },
    staleTime: 60000, // 1 minute
    retry: 2,
  });

  useEffect(() => {
    if (error) {
      toast.error('Failed to load audit logs', {
        description: error instanceof Error ? error.message : 'An unknown error occurred',
        action: {
          label: i18n.buttons.retry,
          onClick: () => refetch(),
        },
      });
    }
  }, [error, refetch, i18n.buttons.retry]);

  // Filter, sort and paginate logs
  const filteredLogs = React.useMemo(() => {
    let result = [...allLogs];

    // Apply filters
    if (filters.action && filters.action !== 'all') {
      result = result.filter(log => log.action === filters.action);
    }

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter(
        log =>
          log.user?.name?.toLowerCase().includes(searchLower) ||
          false ||
          log.user?.email?.toLowerCase().includes(searchLower) ||
          false ||
          log.action.toLowerCase().includes(searchLower) ||
          JSON.stringify(log.details).toLowerCase().includes(searchLower)
      );
    }

    if (filters.dateRange && filters.dateRange !== 'all') {
      const now = new Date();
      let cutoffDate: Date;

      switch (filters.dateRange) {
        case 'today':
          cutoffDate = new Date(now.setHours(0, 0, 0, 0));
          break;
        case 'week':
          cutoffDate = new Date(now);
          cutoffDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          cutoffDate = new Date(now);
          cutoffDate.setMonth(now.getMonth() - 1);
          break;
        default:
          cutoffDate = new Date(0); // Beginning of time
      }

      result = result.filter(log => new Date(log.timestamp) >= cutoffDate);
    }

    // Apply sorting
    result.sort((a, b) => {
      let valueA, valueB;

      switch (sortField) {
        case 'timestamp':
          valueA = new Date(a.timestamp).getTime();
          valueB = new Date(b.timestamp).getTime();
          break;
        case 'action':
          valueA = a.action;
          valueB = b.action;
          break;
        case 'user':
          valueA = a.user?.name || a.user?.email || '';
          valueB = b.user?.name || b.user?.email || '';
          break;
        default:
          valueA = new Date(a.timestamp).getTime();
          valueB = new Date(b.timestamp).getTime();
      }

      const comparison =
        typeof valueA === 'string'
          ? valueA.localeCompare(valueB as string)
          : (valueA as number) - (valueB as number);

      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [allLogs, filters, sortField, sortOrder]);

  // Calculate pagination
  const totalItems = filteredLogs.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const startIndex = (page - 1) * pageSize;
  const paginatedLogs = filteredLogs.slice(startIndex, startIndex + pageSize);

  // Function to handle sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle order if already sorting by this field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to descending
      setSortField(field);
      setSortOrder('desc');
    }
  };

  // Function for refreshing data
  const handleRefresh = () => {
    refetch();
    toast.success('Refreshing audit logs');
  };

  // Function for downloading logs (mock implementation)
  const handleDownload = () => {
    // In a real implementation, this would call an API endpoint
    // that returns a CSV or Excel file
    toast.success('Downloading audit logs', {
      description: 'Your download will begin shortly',
    });

    // Create a CSV from the filtered logs
    const headers = 'ID,User,Action,Target,Date,Details\n';
    const csvContent = filteredLogs
      .map(log => {
        const user = log.user ? `${log.user.name || log.user.email}` : 'System';
        const date = new Date(log.timestamp).toISOString();
        const details = JSON.stringify(log.details).replace(/"/g, '""'); // Escape quotes for CSV

        return `${log.id},"${user}",${log.action},${log.target_type}-${log.target_id},"${date}","${details}"`;
      })
      .join('\n');

    const blob = new Blob([headers + csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `team-audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-enterprise-900 dark:text-white">
            {i18n.history.pageTitle}
          </h1>
          <p className="mt-1 text-enterprise-600 dark:text-gray-300">
            {i18n.history.pageDescription}
          </p>
        </div>
        <Link to={ROUTES.APP.TEAM.ROOT}>
          <Button variant="outline" className="flex items-center">
            <ArrowLeft className="mr-2 size-4" />
            {i18n.buttons.backToTeam}
          </Button>
        </Link>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-2.5 top-2.5 size-4 text-gray-500" />
          <Input
            type="search"
            placeholder={i18n.history.searchPlaceholder}
            className="pl-8"
            value={searchInput}
            onChange={handleSearchChange}
          />
        </div>

        <div className="flex w-full justify-between gap-2 sm:w-auto sm:justify-end">
          <FilterSheet
            filters={filters}
            onFiltersChange={newFilters => {
              setFilters(newFilters);
              setPage(1); // Reset to first page on filter change
            }}
          />

          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={isLoading || isFetching}
            aria-label="Refresh audit logs"
            className="flex items-center gap-2"
          >
            <RefreshCw className={`size-4 ${isFetching ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">{i18n.buttons.refresh}</span>
          </Button>

          <Button
            variant="outline"
            onClick={handleDownload}
            aria-label="Download audit logs"
            className="flex items-center gap-2"
          >
            <DownloadCloud className="size-4" />
            <span className="hidden sm:inline">{i18n.history.exportData}</span>
          </Button>
        </div>
      </div>

      {/* Sorting Header */}
      {!isLoading && !isError && filteredLogs.length > 0 && (
        <div className="sticky top-0 z-10 grid grid-cols-4 gap-4 rounded-t-lg border-b bg-card px-4 py-3 text-sm font-medium text-muted-foreground shadow-sm">
          <button
            className="col-span-1 flex items-center justify-start transition-colors hover:text-foreground"
            onClick={() => handleSort('timestamp')}
            aria-label="Sort by date"
          >
            {i18n.history.dateTime}
            <span
              className={`ml-1 transition-opacity duration-200 ${sortField === 'timestamp' ? 'opacity-100' : 'opacity-30'}`}
            >
              {sortField === 'timestamp' && sortOrder === 'asc' ? (
                <ChevronUp className="size-4" />
              ) : (
                <ChevronDown className="size-4" />
              )}
            </span>
          </button>

          <button
            className="col-span-1 flex items-center justify-start transition-colors hover:text-foreground"
            onClick={() => handleSort('action')}
            aria-label="Sort by action"
          >
            {i18n.history.action}
            <span
              className={`ml-1 transition-opacity duration-200 ${sortField === 'action' ? 'opacity-100' : 'opacity-30'}`}
            >
              {sortField === 'action' && sortOrder === 'asc' ? (
                <ChevronUp className="size-4" />
              ) : (
                <ChevronDown className="size-4" />
              )}
            </span>
          </button>

          <button
            className="col-span-2 flex items-center justify-start transition-colors hover:text-foreground"
            onClick={() => handleSort('user')}
            aria-label="Sort by user"
          >
            {i18n.history.user}
            <span
              className={`ml-1 transition-opacity duration-200 ${sortField === 'user' ? 'opacity-100' : 'opacity-30'}`}
            >
              {sortField === 'user' && sortOrder === 'asc' ? (
                <ChevronUp className="size-4" />
              ) : (
                <ChevronDown className="size-4" />
              )}
            </span>
          </button>
        </div>
      )}

      {/* Loading state */}
      {isLoading && <AuditLogSkeleton />}

      {/* Error state */}
      {isError && !isLoading && (
        <div className="rounded-lg bg-card p-8 text-center shadow">
          <AlertCircle className="mx-auto mb-4 size-12 text-red-500" />
          <h3 className="mb-2 text-lg font-semibold">{i18n.errors.unableToLoadAuditLogs}</h3>
          <p className="mb-4 text-gray-600">
            {error instanceof Error ? error.message : i18n.errors.unknownErrorOccurred}
          </p>
          <Button variant="outline" onClick={() => refetch()} className="mx-auto">
            {i18n.buttons.tryAgain}
          </Button>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !isError && filteredLogs.length === 0 && (
        <EmptyState onRefresh={handleRefresh} />
      )}

      {/* Logs list */}
      {!isLoading && !isError && filteredLogs.length > 0 && (
        <div className="space-y-6 rounded-b-lg bg-muted/30 shadow">
          {groupLogsByDate(paginatedLogs).map(([dateGroup, logsInGroup]) => (
            <div key={dateGroup} className="space-y-2">
              <Accordion type="single" collapsible defaultValue={dateGroup}>
                <AccordionItem value={dateGroup} className="border-0">
                  <AccordionTrigger className="bg-muted/50 px-4 py-2 text-sm font-semibold text-foreground hover:no-underline">
                    {dateGroup} ({logsInGroup.length}{' '}
                    {logsInGroup.length === 1 ? i18n.history.entry : i18n.history.entries})
                  </AccordionTrigger>
                  <AccordionContent className="px-0 pb-0 pt-2">
                    <div className="space-y-2">
                      {logsInGroup.map((log: AuditLogEntry) => (
                        <div
                          key={log.id}
                          className="mx-2 rounded-lg border bg-card p-4 
                                   transition-all duration-200 hover:shadow-md"
                        >
                          <div className="grid grid-cols-4 gap-4">
                            <div className="col-span-1 flex items-center text-sm text-muted-foreground">
                              <Clock className="mr-1 size-3 shrink-0" />
                              <span title={new Date(log.timestamp).toLocaleString()}>
                                {format(new Date(log.timestamp), 'h:mm a')}
                              </span>
                            </div>

                            <div className="col-span-1">
                              <ActionBadge action={log.action} />
                            </div>

                            <div className="col-span-2">
                              <div className="text-sm">
                                <span className="font-medium">
                                  {log.user
                                    ? (log.user.name ?? log.user.email)
                                    : i18n.history.system}
                                </span>{' '}
                                <ActionDescription log={log} />
                              </div>
                            </div>
                          </div>

                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewDetails(log)}
                            className="mt-2 h-7 text-xs text-muted-foreground hover:text-foreground"
                          >
                            {i18n.history.viewDetails}
                          </Button>
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          ))}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between rounded-b-lg border-t bg-card p-4">
              <div className="text-sm text-muted-foreground">
                {i18n.history.pagination.showing(
                  startIndex + 1,
                  Math.min(startIndex + pageSize, totalItems),
                  totalItems
                )}
              </div>
              <PaginationControl
                currentPage={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </div>
          )}
        </div>
      )}

      {/* Details Drawer */}
      <DetailDrawer log={selectedLog} open={detailsOpen} onClose={handleCloseDetails} />
    </div>
  );
}
